import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { MessageCircle, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

function useWhatsApp() {
  const { data } = useQuery({
    queryKey: ["whatsapp-group"],
    queryFn: async () => (await api.get("/whatsapp-group")).data,
  });
  return data || {};
}

// Compact button for embedding inside cards (e.g. RSVP confirmation screens).
export function WhatsAppJoinButton() {
  const { t } = useLang();
  const { url } = useWhatsApp();
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-join-btn"
      className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-6 py-3 transition-colors"
    >
      <MessageCircle size={18} /> <EditableText k="wa.btn" as="span" />
    </a>
  );
}

// Site-colored (navy + gold) strip for placing above the footer on interior
// pages (e.g. event detail). Same text/link source as the homepage strip.
export function WhatsAppJoinStrip() {
  const { t, lang, getOverride } = useLang();
  const data = useWhatsApp();
  const url = data.url?.trim() || "";
  if (!url) return null;

  const custom = (key) => {
    const v = lang === "en" ? data[key] : data.i18n?.[lang]?.[key];
    return (v || "").trim();
  };
  const pick = (waKey, txtKey) => {
    const ov = getOverride(txtKey);
    if (ov !== undefined) return ov.trim();
    return custom(waKey) || t(txtKey);
  };
  const title = pick("title", "wa.title");
  const desc = pick("desc", "wa.desc");
  const cta = pick("cta", "wa.cta") || t("wa.btn");

  return (
    <section className="relative z-10 pb-14 sm:pb-20" data-testid="event-whatsapp-strip">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="event-whatsapp-strip-btn"
          className="group flex items-center gap-3 sm:gap-4 w-full rounded-2xl bg-[#0F172A] px-4 sm:px-6 py-3.5 hover:bg-[#152238] transition-colors"
        >
          <span className="shrink-0 w-9 h-9 rounded-xl bg-[#BE923C] flex items-center justify-center text-[#0F172A] group-hover:scale-105 transition-transform">
            <MessageCircle size={18} />
          </span>
          <span className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:gap-2">
            <span className="font-serif text-base sm:text-lg text-[#F9F8F6] leading-tight truncate">{title}</span>
            {desc && <span className="hidden md:inline text-sm text-[#BE923C]/70">·</span>}
            {desc && <span className="text-xs sm:text-sm text-white/65 truncate leading-tight">{desc}</span>}
          </span>
          <span className="ms-auto shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#BE923C] group-hover:bg-[#d0a54a] text-[#0F172A] text-sm font-semibold px-5 sm:px-6 py-2 transition-colors">
            {cta} <ArrowUpRight size={15} className="rtl:rotate-[-90deg]" />
          </span>
        </a>
      </div>
    </section>
  );
}

export function WhatsAppJoin() {
  const { t, lang, getOverride } = useLang();
  const data = useWhatsApp();
  const url = data.url?.trim() || "";
  if (!url) return null;

  // Priority: Site Text override (global, authoritative) → dedicated WhatsApp
  // admin custom text → translated default.
  const custom = (key) => {
    const v = lang === "en" ? data[key] : data.i18n?.[lang]?.[key];
    return (v || "").trim();
  };
  const pick = (waKey, txtKey) => {
    const ov = getOverride(txtKey);
    if (ov !== undefined) return ov.trim();
    return custom(waKey) || t(txtKey);
  };
  const title = pick("title", "wa.title");
  const desc = pick("desc", "wa.desc");
  const cta = pick("cta", "wa.cta");

  return (
    <section className="relative z-10 pt-0 pb-14 sm:pb-20 -mt-16 sm:-mt-24" data-testid="whatsapp-join-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-join-btn"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="group flex items-center gap-3 sm:gap-4 w-full rounded-2xl bg-[#0F172A] outline outline-1 outline-offset-[12px] outline-[#BE923C]/80 px-4 sm:px-6 py-3 sm:py-3.5 hover:bg-[#152238] hover:outline-[#BE923C] transition-[background-color,outline-color] duration-300"
        >
          <span className="shrink-0 w-9 h-9 rounded-xl bg-[#25D366] flex items-center justify-center text-white shadow-[0_2px_12px_rgba(37,211,102,0.45)] group-hover:scale-105 transition-transform">
            <MessageCircle size={18} />
          </span>
          <span className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:gap-2">
            <span className="font-serif text-base sm:text-lg text-[#F9F8F6] leading-tight truncate">{title}</span>
            {desc && <span className="hidden md:inline text-sm text-[#BE923C]/70">·</span>}
            {desc && <span className="text-xs sm:text-sm text-white/65 truncate leading-tight">{desc}</span>}
          </span>
          <span className="ms-auto shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#BE923C] group-hover:bg-[#d0a54a] text-[#0F172A] text-sm font-semibold px-4 sm:px-6 py-2 transition-colors">
            {cta} <ArrowUpRight size={15} className="rtl:rotate-[-90deg]" />
          </span>
        </motion.a>
      </div>
    </section>
  );
}
